export type Obj = Record<string, unknown>

export interface Entry {
  object: Obj
  pathParts: string[]
  path: string
  parents: Obj[]
}

export interface TraverseAndCollectRefsInput {
  source: Obj
  // To determine which entries to traverse deeper into (used to short-circuit traversal, i.e. optimization only)
  shouldTraverse: (entry: Entry) => boolean
  // To determine which entries to collect and return
  shouldCollect: (entry: Entry) => boolean
}
export function traverseAndCollectRefs({
  source,
  shouldTraverse,
  shouldCollect,
}: TraverseAndCollectRefsInput): Map<string, Entry> {
  const basePath: string[] = []
  const refs = new Map<string, Entry>()
  const stack: Array<Entry> = []

  // Initialize stack with root entries
  for (const [rootKey, rootValue] of Object.entries(source)) {
    const pathArray = [...basePath, rootKey]

    stack.push({
      object: rootValue as Obj,
      pathParts: pathArray,
      path: pathArray.join('.'),
      parents: [],
    })
  }

  while (stack.length > 0) {
    const entry = stack.pop()!
    const { object: current, pathParts: path, parents } = entry

    if (!(current && typeof current === 'object' && !Array.isArray(current))) {
      continue
    }

    const entryToProcess = { ...entry, object: current }

    if (shouldCollect(entryToProcess)) {
      refs.set(path.join('.'), entryToProcess)
    }

    // Only traverse deeper if we should traverse OR if it's the root level
    // (we always want to traverse from root even if root doesn't match shouldTraverse)
    const isRoot = path.length === 1
    if (!(shouldTraverse(entryToProcess) || isRoot)) {
      continue
    }

    // Add child entries to stack (in reverse order to maintain original traversal order)
    const entries = Object.entries(current).reverse()
    for (const [key, value] of entries) {
      const newPath = [...path, key]
      const newParents = [...parents, current]

      if (Array.isArray(value)) {
        // Add array items to stack in reverse order
        for (let index = value.length - 1; index >= 0; index--) {
          const pathParts = [...newPath, index.toString()]
          stack.push({
            object: value[index] as Obj,
            pathParts,
            path: pathParts.join('.'),
            parents: newParents,
          })
        }
      } else if (value && typeof value === 'object') {
        stack.push({
          object: value as Obj,
          pathParts: newPath,
          path: newPath.join('.'),
          parents: newParents,
        })
      }
    }
  }

  return refs
}
